/**
 * usePendingFormsCount — Returns the count of acceptance forms pending the current user's signature.
 * Polls every 60 seconds and also refreshes on socket notifications.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import * as hwApi from '../api/hardwareApi';
import socket from '../socket';
import { useAuth } from '../context/AuthContext';

export default function usePendingFormsCount() {
  const { user } = useAuth();
  const [count, setCount] = useState(0);
  const intervalRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!user) { setCount(0); return; }
    try {
      const { data } = await hwApi.fetchMyPendingForms();
      setCount(data.total ?? data.documents?.length ?? 0);
    } catch {
      // silent — don't break the sidebar if this fails
    }
  }, [user]);

  useEffect(() => {
    fetchCount();

    // Poll every 60 seconds
    intervalRef.current = setInterval(fetchCount, 60_000);

    // Listen for real-time notifications that may indicate a new form
    const onNotification = (notif) => {
      if (notif.type === 'acceptance_form_pending_signature' ||
          notif.type === 'acceptance_form_ready') {
        fetchCount();
      }
    };
    socket.on('notification', onNotification);

    return () => {
      clearInterval(intervalRef.current);
      socket.off('notification', onNotification);
    };
  }, [fetchCount]);

  return count;
}
